import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { 
  Building2, ArrowLeft, Users, CreditCard, Save, 
  Pause, Play, Trash2, UserPlus, Mail, Phone,
  Calendar, Clock, AlertTriangle
} from 'lucide-react';
import { superAdminApi } from '../../utils/superAdminApi';
import { toast } from 'sonner';
import { useConfirm } from '../../context/ConfirmContext';

export const TenantDetailPage = () => {
  const confirm = useConfirm();
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedTenant, setEditedTenant] = useState({});
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: ''
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [tenantRes, usersRes, plansRes] = await Promise.all([
        superAdminApi.getTenant(id),
        superAdminApi.getTenantUsers(id),
        superAdminApi.getPlans()
      ]);
      setTenant(tenantRes.data);
      setEditedTenant(tenantRes.data);
      setUsers(usersRes.data);
      setPlans(plansRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
      navigate('/super-admin/tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await superAdminApi.updateTenant(id, {
        company_name: editedTenant.company_name,
        contact_email: editedTenant.contact_email,
        contact_phone: editedTenant.contact_phone,
        plan_id: editedTenant.plan_id,
        billing_cycle: editedTenant.billing_cycle,
        notes: editedTenant.notes
      });
      toast.success('Modifiche salvate');
      setEditMode(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleSuspend = async () => {
    const ok = await confirm({
      title: 'Sospendi azienda',
      description: 'L\'azienda sarà sospesa e gli utenti non potranno più accedere finché non verrà riattivata.',
      confirmText: 'Sospendi',
      destructive: true,
    });
    if (!ok) return;
    try {
      await superAdminApi.suspendTenant(id);
      toast.success('Azienda sospesa');
      loadData();
    } catch (error) {
      toast.error('Errore nella sospensione');
    }
  };

  const handleActivate = async () => {
    try {
      await superAdminApi.activateTenant(id);
      toast.success('Azienda attivata');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'attivazione');
    }
  };

  const handleDelete = async () => {
    const confirmText = tenant.company_name;
    const input = window.prompt(`Questa azione è IRREVERSIBILE. Tutti i dati verranno eliminati.\n\nDigita "${confirmText}" per confermare:`);
    if (input !== confirmText) {
      toast.error('Eliminazione annullata');
      return;
    }
    try {
      await superAdminApi.deleteTenant(id);
      toast.success('Azienda eliminata');
      navigate('/super-admin/tenants');
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await superAdminApi.createTenantAdmin(id, newUser);
      toast.success('Utente admin creato');
      setUserDialogOpen(false);
      setNewUser({ email: '', password: '', first_name: '', last_name: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione utente');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50';
      case 'TRIAL': return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      case 'SUSPENDED': return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 'CANCELLED': return 'bg-slate-500/20 text-slate-400 border-slate-500/50';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/50';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'ACTIVE': return 'Attivo';
      case 'TRIAL': return 'Trial';
      case 'SUSPENDED': return 'Sospeso';
      case 'CANCELLED': return 'Cancellato';
      default: return status;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!tenant) return null;

  return (
    <div className="space-y-6" data-testid="tenant-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/super-admin/tenants')}
            className="text-slate-300 hover:text-white"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-white">{tenant.company_name}</h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(tenant.status)}`}>
                {getStatusLabel(tenant.status)}
              </span>
            </div>
            <p className="text-slate-400 mt-1">ID: {tenant.id}</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          {editMode ? (
            <>
              <Button variant="ghost" onClick={() => {setEditMode(false); setEditedTenant(tenant);}}>
                Annulla
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900"
              >
                <Save size={18} className="mr-2" />
                {saving ? 'Salvataggio...' : 'Salva'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setEditMode(true)} className="border-slate-600">
                Modifica
              </Button>
              {tenant.status === 'ACTIVE' ? (
                <Button 
                  variant="outline" 
                  className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                  onClick={handleSuspend}
                >
                  <Pause size={18} className="mr-2" />
                  Sospendi
                </Button>
              ) : tenant.status === 'SUSPENDED' && (
                <Button 
                  variant="outline" 
                  className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
                  onClick={handleActivate}
                >
                  <Play size={18} className="mr-2" />
                  Attiva
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-amber-500" />
                Informazioni Azienda
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-400">Nome Azienda</Label>
                  {editMode ? (
                    <Input
                      value={editedTenant.company_name}
                      onChange={(e) => setEditedTenant({...editedTenant, company_name: e.target.value})}
                      className="bg-slate-700 border-slate-600"
                    />
                  ) : (
                    <p className="text-white">{tenant.company_name}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400">Email Contatto</Label>
                  {editMode ? (
                    <Input
                      type="email"
                      value={editedTenant.contact_email}
                      onChange={(e) => setEditedTenant({...editedTenant, contact_email: e.target.value})}
                      className="bg-slate-700 border-slate-600"
                    />
                  ) : (
                    <p className="text-white flex items-center gap-2">
                      <Mail size={16} className="text-slate-400" />
                      {tenant.contact_email}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400">Telefono</Label>
                  {editMode ? (
                    <Input
                      value={editedTenant.contact_phone || ''}
                      onChange={(e) => setEditedTenant({...editedTenant, contact_phone: e.target.value})}
                      className="bg-slate-700 border-slate-600"
                    />
                  ) : (
                    <p className="text-white flex items-center gap-2">
                      <Phone size={16} className="text-slate-400" />
                      {tenant.contact_phone || '-'}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-400">Note</Label>
                {editMode ? (
                  <Textarea
                    value={editedTenant.notes || ''}
                    onChange={(e) => setEditedTenant({...editedTenant, notes: e.target.value})}
                    className="bg-slate-700 border-slate-600"
                    rows={3}
                  />
                ) : (
                  <p className="text-slate-300">{tenant.notes || 'Nessuna nota'}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Users */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-500" />
                Utenti ({users.length}{tenant.max_users > 0 ? ` / ${tenant.max_users}` : ''})
              </CardTitle>
              <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                    <UserPlus size={16} className="mr-2" />
                    Aggiungi Admin
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-800 border-slate-700 text-white">
                  <DialogHeader>
                    <DialogTitle>Crea Utente Admin</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateUser} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300">Nome *</Label>
                        <Input
                          value={newUser.first_name}
                          onChange={(e) => setNewUser({...newUser, first_name: e.target.value})}
                          required
                          className="bg-slate-700 border-slate-600"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Cognome *</Label>
                        <Input
                          value={newUser.last_name}
                          onChange={(e) => setNewUser({...newUser, last_name: e.target.value})}
                          required
                          className="bg-slate-700 border-slate-600"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Email *</Label>
                      <Input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                        required
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Password *</Label>
                      <Input
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                        required
                        minLength={6}
                        className="bg-slate-700 border-slate-600"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="ghost" onClick={() => setUserDialogOpen(false)}>
                        Annulla
                      </Button>
                      <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                        Crea Utente
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {users.length === 0 ? (
                <p className="text-slate-400 text-center py-4">Nessun utente</p>
              ) : (
                <div className="space-y-2">
                  {users.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-700/50">
                      <div>
                        <p className="font-medium text-white">{user.first_name} {user.last_name}</p>
                        <p className="text-sm text-slate-400">{user.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 rounded text-xs font-medium bg-slate-600 text-slate-300">
                          {user.role}
                        </span>
                        {!user.active && (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-red-500/20 text-red-400">
                            Disattivo
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Subscription */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-amber-500" />
                Abbonamento
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-400">Piano</Label>
                {editMode ? (
                  <Select 
                    value={editedTenant.plan_id} 
                    onValueChange={(v) => setEditedTenant({...editedTenant, plan_id: v})}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      {plans.map(plan => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {plan.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-xl font-bold text-amber-500">{tenant.plan_name || '-'}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-slate-400">Ciclo Fatturazione</Label>
                {editMode ? (
                  <Select 
                    value={editedTenant.billing_cycle} 
                    onValueChange={(v) => setEditedTenant({...editedTenant, billing_cycle: v})}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-700 border-slate-600">
                      <SelectItem value="MONTHLY">Mensile</SelectItem>
                      <SelectItem value="YEARLY">Annuale</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-white">{tenant.billing_cycle === 'YEARLY' ? 'Annuale' : 'Mensile'}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-slate-400">Limite Utenti</Label>
                <p className="text-white">
                  {tenant.max_users < 0 ? 'Illimitati' : tenant.max_users}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Cronologia
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-3">
                <Calendar size={16} className="text-slate-400 mt-1" />
                <div>
                  <p className="text-sm text-slate-400">Creato</p>
                  <p className="text-white">{formatDate(tenant.created_at)}</p>
                  {tenant.created_by && (
                    <p className="text-xs text-slate-500">da {tenant.created_by}</p>
                  )}
                </div>
              </div>

              {tenant.suspended_at && (
                <div className="flex items-start gap-3">
                  <Pause size={16} className="text-red-400 mt-1" />
                  <div>
                    <p className="text-sm text-slate-400">Sospeso</p>
                    <p className="text-white">{formatDate(tenant.suspended_at)}</p>
                    {tenant.suspended_by && (
                      <p className="text-xs text-slate-500">da {tenant.suspended_by}</p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="bg-red-950/30 border-red-500/30">
            <CardHeader>
              <CardTitle className="text-red-400 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Zona Pericolosa
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-400 mb-4">
                Elimina permanentemente questa azienda e tutti i suoi dati. Questa azione è irreversibile.
              </p>
              <Button 
                variant="outline" 
                className="w-full border-red-500/50 text-red-400 hover:bg-red-500/20"
                onClick={handleDelete}
              >
                <Trash2 size={16} className="mr-2" />
                Elimina Azienda
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
